// Desktop auth projection (sdk ADR-012).
import { acquisitionSourceOf, blocksUI, type HolderPhase } from '@localagent/sdk';
import { DesktopWindowChrome } from '../components/layout/DesktopWindowChrome';
import { phaseIndicator, type PhaseIndicatorKind } from './phaseIndicator';

export interface DaemonSessionOverlayProps {
  phase: HolderPhase;
  /** True while `POST /auth/begin-signin` + browser open are in flight. */
  pending?: boolean;
  /** A local sign-in failure (IPC bridge missing, agent offline). */
  signInError?: string | null;
  onSignIn?: () => void;
}

const INDICATOR_TEST_IDS: Record<PhaseIndicatorKind, string> = {
  activating: 'daemon-auth-activating',
  renewing: 'daemon-auth-renewing',
  reconnecting: 'daemon-auth-reconnecting',
};

function GateChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-background">
      <DesktopWindowChrome>{children}</DesktopWindowChrome>
    </div>
  );
}

function QuietIndicator({ testId, text }: { testId: string; text: string }) {
  return (
    <div
      data-testid={testId}
      role="status"
      className="pointer-events-none fixed bottom-4 right-4 z-40 rounded-full border border-border bg-card/90 px-3 py-1 text-caption text-foreground-secondary shadow"
    >
      {text}
    </div>
  );
}

export function DaemonSessionOverlay({
  phase,
  pending = false,
  signInError = null,
  onSignIn,
}: DaemonSessionOverlayProps) {
  if (!blocksUI(phase)) {
    const indicator = phaseIndicator(phase);
    if (indicator === null) return null;
    return (
      <QuietIndicator testId={INDICATOR_TEST_IDS[indicator.kind]} text={indicator.text} />
    );
  }

  const waiting = acquisitionSourceOf(phase) !== null;
  const buttonLabel = waiting
    ? 'Open browser again'
    : pending
      ? 'Opening browser…'
      : 'Sign in to ShogunChop';
  return (
    <GateChrome>
      <div
        data-testid="desktop-signin-prompt"
        className="flex h-full min-h-full flex-col items-center justify-center gap-4 bg-muted px-4"
      >
        <h1 className="text-display font-display font-semibold">ShogunChop</h1>
        <p className="max-w-md text-center text-body text-foreground-secondary">
          Sign in to connect this device to your ShogunChop organization. Your system
          browser will open to complete sign-in.
        </p>
        {waiting && (
          <p
            data-testid="desktop-signin-waiting"
            className="max-w-md text-center text-body text-foreground-secondary"
          >
            Waiting for browser… Complete sign-in in your browser, or click below to
            open it again.
          </p>
        )}
        {pending && !waiting && (
          <p
            data-testid="desktop-signin-opening"
            className="max-w-md text-center text-body text-foreground-secondary"
          >
            Opening browser…
          </p>
        )}
        <button
          data-testid="desktop-signin-button"
          type="button"
          disabled={pending}
          onClick={onSignIn}
          className="rounded bg-primary px-6 py-2 text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {buttonLabel}
        </button>
        {signInError && (
          <div
            data-testid="desktop-signin-error"
            className="flex flex-col items-center gap-2 text-body text-error-text"
          >
            <span>{signInError}</span>
          </div>
        )}
      </div>
    </GateChrome>
  );
}
